<template>
  <div class="network-monitor">
    <v-dialog v-model="authFailureDialog" persistent max-width="366" transition="dialog-top-transition">
      <v-card class="d-flex flex-column rounded-xl pa-6 text-center gap-2">
        <div class="d-flex justify-center align-center">
          <div class="d-flex justify-center align-center pa-3 rounded-circle overflow-hidden auth-failure-icon">
            <v-img :src="authFailureImage" contain height="72" width="72"></v-img>
          </div>
        </div>
        <v-card-title class="font-weight-bold justify-center align-center text-h6 py-0 px-0">
          No Internet Connectivity
        </v-card-title>
        <v-card-text class="d-flex justify-center align-center py-0 px-0 auth-failure-subtext">
          You are currently offline. Please reconnect to continue the assessment, while your progress is being saved
        </v-card-text>
        <v-card-actions class="justify-center align-center px-0">
          <v-btn height="48px" width="70%" elevation="0" class="btn primary-btn elevation-0" rounded @click="handleAuthFailureDismiss">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="networkSnackbar.show"
      :timeout="networkSnackbar.timeout"
      :class="'network-snackbar--' + networkSnackbar.type"
      color="white"
      top right
      z-index="500"
    >
      <div class="d-flex align-center gap-4" :style="{ color: networkSnackbar.textColor }">
        <v-img class="network-snackbar-icon" :src="networkSnackbar.image" contain height="20" width="20"></v-img>
        <span>{{ networkSnackbar.text }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon v-bind="attrs" @click="networkSnackbar.show = false" :color="networkSnackbar.textColor">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import AuthService from "@/services/AuthService";

const SNACKBAR_TIMEOUT_MS = 3000;
const NETWORK_STATES = {
  offline: {
    isPoor: true,
    type: 'offline',
    textColor: '#E6393E',
    text: 'You are currently offline. Please reconnect to continue the assessment, while your progress is being saved',
    image: require('@/assets/disconnected-network.svg'),
  },
  weak: {
    isPoor: true,
    type: 'weak',
    textColor: '#E48500',
    text: 'Your internet connection is weak. Please ensure a stable connection for a smooth experience',
    image: require('@/assets/weak-network.svg'),
  },
  restored: {
    isPoor: false,
    type: 'restored',
    textColor: '#06C270',
    text: 'You are back online. Please continue with your assessment',
    image: require('@/assets/restored-network.svg'),
  },
};

export default {
  name: 'NetworkMonitor',

  data: () => ({
    isNetworkPoor: false,
    authFailureDialog: false,
    authFailureImage: NETWORK_STATES.offline.image,
    networkSnackbar: {
      show: false,
      text: '',
      image: '',
      type: '',
      textColor: '',
      timeout: -1,
    },
  }),
  computed: {
    userType() {
      return this.$store.getters.userType;
    },
  },
  methods: {
    bindNetworkListeners() {
      const connection = this.getConnection();

      window.addEventListener('online', this.handleNetworkStatusChange);
      window.addEventListener('offline', this.handleNetworkStatusChange);
      window.addEventListener('app-network-error', this.handleAxiosNetworkError);
      window.addEventListener('app-auth-failure', this.handleAuthFailureEvent);

      if (connection && connection.addEventListener) {
        connection.addEventListener('change', this.handleNetworkStatusChange);
      }
    },
    unbindNetworkListeners() {
      const connection = this.getConnection();

      window.removeEventListener('online', this.handleNetworkStatusChange);
      window.removeEventListener('offline', this.handleNetworkStatusChange);
      window.removeEventListener('app-network-error', this.handleAxiosNetworkError);
      window.removeEventListener('app-auth-failure', this.handleAuthFailureEvent);

      if (connection && connection.removeEventListener) {
        connection.removeEventListener('change', this.handleNetworkStatusChange);
      }
    },
    handleNetworkStatusChange() {
      const status = this.getNetworkStatus();
      const wasNetworkPoor = this.isNetworkPoor;

      this.isNetworkPoor = status.isPoor;

      if (status.isPoor) {
        this.showNetworkSnackbar(status);
        return;
      }

      if (wasNetworkPoor) {
        this.showNetworkSnackbar(status);
        return;
      }

      if (this.networkSnackbar.type !== 'restored') {
        this.networkSnackbar.show = false;
      }
    },
    handleAxiosNetworkError() {
      if (!navigator.onLine) {
        this.handleNetworkStatusChange();
        return;
      }

      this.isNetworkPoor = true;
      this.showNetworkSnackbar(NETWORK_STATES.weak);
    },
    showNetworkSnackbar(status) {
      this.networkSnackbar = {
        show: true,
        text: status.text,
        image: status.image,
        type: status.type,
        textColor: status.textColor,
        timeout: status.isPoor ? -1 : SNACKBAR_TIMEOUT_MS,
      };
    },
    getNetworkStatus() {
      const connection = this.getConnection();
      const slowEffectiveTypes = ['slow-2g', '2g'];

      if (!navigator.onLine) {
        return NETWORK_STATES.offline;
      }
      if (
        connection &&
        (
          slowEffectiveTypes.includes(connection.effectiveType) ||
          (typeof connection.downlink === 'number' && connection.downlink <= 0.2) ||
          (typeof connection.rtt === 'number' && connection.rtt >= 2500)
        )
      ) {
        return NETWORK_STATES.weak;
      }
      return NETWORK_STATES.restored;
    },
    getConnection() {
      return navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    },
    preloadNetworkImages() {
      Object.values(NETWORK_STATES).forEach(state => {
        const img = new Image();
        img.src = state.image;
      });
    },
    handleAuthFailureEvent() {
      this.authFailureDialog = true;
    },
    async handleAuthFailureDismiss() {
      this.authFailureDialog = false;
      this.$emit('force-navigate');
      const cachedUrl = this.$store.getters.sourceUrl;
      if (cachedUrl) {
        window.location.replace(cachedUrl);
      }
      else if (this.userType) {
        // other external user types
        window.close();
      }
      else {
        // internal user
        AuthService.logout();
        window.location.href = '/#/login';
      }
    },
  },
  created() {
    this.preloadNetworkImages();
  },
  mounted() {
    this.bindNetworkListeners();
    this.handleNetworkStatusChange();
  },
  beforeDestroy() {
    this.unbindNetworkListeners();
  },
};
</script>

<style scoped>
::v-deep .v-snack__wrapper {
  border-style: solid !important;
  border-width: 0.5px !important;
  box-shadow: 8px 12px 90px 0px #00000066 !important;
  margin: 24px !important;
}

::v-deep .v-snack__content {
  font-size: 16px !important;
}

.auth-failure-icon {
  height: 72px;
  width: 72px;
  background: rgba(230, 57, 62, 0.12);
}

.network-snackbar-icon {
  flex: 0 0 20px;
}

.network-snackbar--offline ::v-deep .v-snack__wrapper {
  background: #FBE3E4 !important;
  border-color: #E6393E !important;
}

.network-snackbar--weak ::v-deep .v-snack__wrapper {
  background: #FFF0DB !important;
  border-color: #FF9500 !important;
}

.network-snackbar--restored ::v-deep .v-snack__wrapper {
  background: #EBFBF4;
  border-color: #06C270 !important;
}
.auth-failure-subtext {
  font-size: 1rem !important;
  font-weight: normal;
  letter-spacing: 0.009375em !important;
  font-family: "Roboto", sans-serif !important;
}
.gap-2{
  gap: 8px !important;
}
.gap-4{
  gap: 16px !important;
}
</style>
